import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Sparkles, AlertCircle } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import * as ss from 'simple-statistics';
import { ParsedWorksheet, WorksheetColumn } from './worksheet';

interface AdvancedGraphBuilderProps {
  worksheet: ParsedWorksheet;
  selectedColumnId?: string;
}

type GraphType = 'histogram' | 'scatter' | 'bubble' | 'run' | 'pareto' | 'heatmap' | 'boxplot';

const palette = ['#2563eb', '#7c3aed', '#059669', '#ea580c', '#db2777', '#0891b2', '#4f46e5'];
const formatNumber = (value: number) => (Number.isFinite(value) ? value.toFixed(4) : '—');

const getHistogram = (values: number[]) => {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const bins = Math.max(6, Math.ceil(Math.sqrt(values.length)));
  const width = (max - min || 1) / bins;
  return Array.from({ length: bins }, (_, index) => {
    const start = min + index * width;
    const end = index === bins - 1 ? max + Number.EPSILON : start + width;
    return {
      label: `${start.toFixed(2)}–${(start + width).toFixed(2)}`,
      count: values.filter((value) => value >= start && value < end).length,
    };
  });
};

const getBoxSummary = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const q1 = ss.quantileSorted(sorted, 0.25);
  const median = ss.quantileSorted(sorted, 0.5);
  const q3 = ss.quantileSorted(sorted, 0.75);
  const scale = (value: number) => ((value - min) / (max - min || 1)) * 100;
  return { min, max, q1, median, q3, minPct: scale(min), q1Pct: scale(q1), medianPct: scale(median), q3Pct: scale(q3), maxPct: scale(max) };
};

const correlationColor = (value: number) => {
  const intensity = Math.round(((value + 1) / 2) * 255);
  const red = value >= 0 ? 20 : 120;
  const blue = value >= 0 ? 180 : 40;
  return `rgb(${red + intensity / 5}, ${235 - intensity / 3}, ${blue + intensity / 4})`;
};

export const AdvancedGraphBuilder: React.FC<AdvancedGraphBuilderProps> = ({ worksheet, selectedColumnId }) => {
  const [graphType, setGraphType] = useState<GraphType>('histogram');
  const [xColumnId, setXColumnId] = useState(selectedColumnId ?? worksheet.numericColumns[0]?.id ?? '');
  const [yColumnId, setYColumnId] = useState(worksheet.numericColumns[1]?.id ?? worksheet.numericColumns[0]?.id ?? '');
  const [sizeColumnId, setSizeColumnId] = useState(worksheet.numericColumns[2]?.id ?? worksheet.numericColumns[0]?.id ?? '');
  const [categoryColumnId, setCategoryColumnId] = useState(worksheet.columns[0]?.id ?? '');

  const xColumn = worksheet.numericColumns.find((column) => column.id === xColumnId) ?? worksheet.numericColumns[0];
  const yColumn = worksheet.numericColumns.find((column) => column.id === yColumnId) ?? worksheet.numericColumns[1] ?? worksheet.numericColumns[0];
  const sizeColumn = worksheet.numericColumns.find((column) => column.id === sizeColumnId) ?? worksheet.numericColumns[2] ?? worksheet.numericColumns[0];
  const categoryColumn = worksheet.columns.find((column) => column.id === categoryColumnId) ?? worksheet.columns[0];

  const histogram = useMemo(() => (xColumn ? getHistogram(xColumn.numericValues) : []), [xColumn]);

  const scatterData = useMemo(() => {
    if (!xColumn || !yColumn) return [];
    const length = Math.min(xColumn.numericValues.length, yColumn.numericValues.length, sizeColumn?.numericValues.length ?? Number.MAX_SAFE_INTEGER);
    return Array.from({ length }, (_, index) => ({
      x: xColumn.numericValues[index],
      y: yColumn.numericValues[index],
      z: sizeColumn?.numericValues[index] ?? 1,
      point: index + 1,
    }));
  }, [sizeColumn, xColumn, yColumn]);

  const pareto = useMemo(() => {
    if (!categoryColumn) return [];
    const counts = categoryColumn.values
      .filter((value) => value.trim() !== '')
      .reduce<Record<string, number>>((acc, value) => {
        acc[value] = (acc[value] ?? 0) + 1;
        return acc;
      }, {});
    const ordered = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([label, count]) => ({ label, count }));
    const total = ordered.reduce((sum, item) => sum + item.count, 0) || 1;
    let running = 0;
    return ordered.map((item) => {
      running += item.count;
      return { ...item, cumulative: (running / total) * 100 };
    });
  }, [categoryColumn]);

  const heatmap = useMemo(() => {
    const columns = worksheet.numericColumns.slice(0, 6);
    return columns.flatMap((columnX) =>
      columns.map((columnY) => {
        const length = Math.min(columnX.numericValues.length, columnY.numericValues.length);
        const dataPairs = Array.from({ length }, (_, index) => [columnX.numericValues[index], columnY.numericValues[index]] as [number, number]);
        const value = length >= 2 ? ss.sampleCorrelation(dataPairs.map((pair) => pair[0]), dataPairs.map((pair) => pair[1])) : 0;
        return { x: columnX.name, y: columnY.name, value: Number.isFinite(value) ? value : 0 };
      }),
    );
  }, [worksheet.numericColumns]);

  const boxSummary = useMemo(() => (xColumn && xColumn.numericValues.length >= 2 ? getBoxSummary(xColumn.numericValues) : null), [xColumn]);

  const graphRecommendations = useMemo(() => {
    const recommendations = [] as string[];
    if (worksheet.numericColumns.length >= 1) recommendations.push('Histogram', 'Run chart', 'Boxplot');
    if (worksheet.numericColumns.length >= 2) recommendations.push('Scatterplot', 'Bubble plot');
    if (worksheet.numericColumns.length >= 3) recommendations.push('Heatmap');
    if (worksheet.columns.length >= 1) recommendations.push('Pareto chart');
    return Array.from(new Set(recommendations)).slice(0, 6);
  }, [worksheet.columns.length, worksheet.numericColumns.length]);

  if (worksheet.columns.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No worksheet data</AlertTitle>
        <AlertDescription>Paste worksheet data to launch advanced graph workflows.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-violet-600 p-3 text-white">
          <Sparkles size={24} />
        </div>
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Badge>Ready</Badge>
            <Badge variant="outline">Advanced Graph Builder</Badge>
          </div>
          <h2 className="text-2xl font-semibold text-foreground">Graph Builder + Advanced Charts</h2>
          <p className="text-muted-foreground">Switch between distribution, relationship, categorical, monitoring, and correlation-oriented chart builders using your worksheet columns.</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Worksheet columns</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold">{worksheet.columnCount}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Numeric columns</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold">{worksheet.numericColumns.length}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Recommended next graphs</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {graphRecommendations.map((item) => <Badge key={item} variant="secondary">{item}</Badge>)}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Graph setup</CardTitle>
          <CardDescription>Choose a graph family and map worksheet columns to visual roles.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <div className="space-y-2 xl:col-span-2">
            <Label>Graph type</Label>
            <Select value={graphType} onValueChange={(value: GraphType) => setGraphType(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="histogram">Histogram</SelectItem>
                <SelectItem value="scatter">Scatterplot</SelectItem>
                <SelectItem value="bubble">Bubble plot</SelectItem>
                <SelectItem value="run">Run chart</SelectItem>
                <SelectItem value="pareto">Pareto chart</SelectItem>
                <SelectItem value="heatmap">Correlation heatmap</SelectItem>
                <SelectItem value="boxplot">Boxplot</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>X / value column</Label>
            <Select value={xColumnId} onValueChange={setXColumnId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {worksheet.numericColumns.map((column) => <SelectItem key={column.id} value={column.id}>{column.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Y column</Label>
            <Select value={yColumnId} onValueChange={setYColumnId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {worksheet.numericColumns.map((column) => <SelectItem key={column.id} value={column.id}>{column.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Size / category column</Label>
            <Select value={graphType === 'pareto' ? categoryColumnId : sizeColumnId} onValueChange={graphType === 'pareto' ? setCategoryColumnId : setSizeColumnId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(graphType === 'pareto' ? worksheet.columns : worksheet.numericColumns).map((column) => (
                  <SelectItem key={column.id} value={column.id}>{column.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {(graphType === 'histogram' || graphType === 'run') && !xColumn && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Need a numeric column</AlertTitle>
          <AlertDescription>Select a numeric worksheet column to render this graph.</AlertDescription>
        </Alert>
      )}

      {graphType === 'histogram' && xColumn && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Histogram — {xColumn.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={histogram}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" interval={0} minTickGap={24} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="hsl(var(--primary))" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {(graphType === 'scatter' || graphType === 'bubble') && xColumn && yColumn && scatterData.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{graphType === 'scatter' ? 'Scatterplot' : 'Bubble plot'} — {xColumn.name} vs {yColumn.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 16, right: 16, bottom: 16, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" dataKey="x" name={xColumn.name} />
                  <YAxis type="number" dataKey="y" name={yColumn.name} />
                  {graphType === 'bubble' && <ZAxis type="number" dataKey="z" range={[60, 500]} name={sizeColumn?.name ?? 'Size'} />}
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                  <Scatter data={scatterData} fill="hsl(var(--primary))" />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {graphType === 'run' && xColumn && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Run chart — {xColumn.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={xColumn.numericValues.map((value, index) => ({ period: index + 1, value, median: ss.median(xColumn.numericValues) }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot />
                  <Line type="monotone" dataKey="median" stroke="hsl(var(--destructive))" dot={false} strokeDasharray="6 6" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {graphType === 'pareto' && categoryColumn && pareto.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Pareto chart — {categoryColumn.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={pareto}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" interval={0} minTickGap={18} />
                  <YAxis yAxisId="count" />
                  <YAxis yAxisId="cumulative" orientation="right" domain={[0, 100]} />
                  <Tooltip />
                  <Bar yAxisId="count" dataKey="count" fill="hsl(var(--primary))" />
                  <Line yAxisId="cumulative" type="monotone" dataKey="cumulative" stroke="hsl(var(--destructive))" strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {graphType === 'heatmap' && worksheet.numericColumns.length >= 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Correlation heatmap</CardTitle>
            <CardDescription>Correlation matrix for up to six numeric worksheet columns.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(worksheet.numericColumns.length, 6)}, minmax(0, 1fr))` }}>
              {heatmap.map((cell) => (
                <div key={`${cell.x}-${cell.y}`} className="rounded-lg p-3 text-sm text-white" style={{ backgroundColor: correlationColor(cell.value) }}>
                  <div className="font-semibold">{cell.x}</div>
                  <div className="text-xs opacity-90">vs {cell.y}</div>
                  <div className="mt-2 text-lg font-bold">{formatNumber(cell.value)}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {graphType === 'boxplot' && boxSummary && xColumn && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Boxplot — {xColumn.name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="relative h-20 rounded-lg bg-muted/50 px-6">
              <div className="absolute top-1/2 h-[2px] -translate-y-1/2 bg-foreground/40" style={{ left: `${boxSummary.minPct}%`, width: `${boxSummary.maxPct - boxSummary.minPct}%` }} />
              <div className="absolute top-1/2 h-10 -translate-y-1/2 rounded-md border-2 border-primary bg-primary/15" style={{ left: `${boxSummary.q1Pct}%`, width: `${boxSummary.q3Pct - boxSummary.q1Pct}%` }} />
              <div className="absolute top-1/2 h-12 w-[2px] -translate-y-1/2 bg-destructive" style={{ left: `${boxSummary.medianPct}%` }} />
              <div className="absolute top-1/2 h-6 w-[2px] -translate-y-1/2 bg-foreground/60" style={{ left: `${boxSummary.minPct}%` }} />
              <div className="absolute top-1/2 h-6 w-[2px] -translate-y-1/2 bg-foreground/60" style={{ left: `${boxSummary.maxPct}%` }} />
            </div>
            <div className="grid gap-3 md:grid-cols-5 text-sm">
              {[
                ['Min', boxSummary.min],
                ['Q1', boxSummary.q1],
                ['Median', boxSummary.median],
                ['Q3', boxSummary.q3],
                ['Max', boxSummary.max],
              ].map(([label, value]) => (
                <div key={String(label)} className="rounded-lg border p-3">
                  <div className="text-muted-foreground">{label}</div>
                  <div className="text-lg font-semibold">{formatNumber(Number(value))}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
